import cloneDeep from 'lodash/cloneDeep'

/**
 * Extracts the overlap between the source object and the comparison object.
 * For objects and arrays, it copies over the source values if the key exists in the comparison.
 * For primitives, it skips copying when the source value equals the target value.
 * @param source
 * @param comparison
 * @returns
 */
export const extractOverlap = <T extends object>(
  source: T,
  comparison: Partial<T>
): Partial<T> => {
  const result: Partial<T> = {}

  for (const key in source) {
    if (key in comparison) {
      const sourceValue = source[key]
      const comparisonValue = comparison[key]

      if (Array.isArray(sourceValue)) {
        // Deep clone the source array
        result[key] = cloneDeep(sourceValue)
      } else if (
        typeof sourceValue === 'object' &&
        sourceValue !== null &&
        typeof comparisonValue === 'object' &&
        comparisonValue !== null
      ) {
        // Recursively handle nested objects
        const nestedOverlap = extractOverlap(sourceValue, comparisonValue)
        if (Object.keys(nestedOverlap).length > 0) {
          result[key] = nestedOverlap as T[Extract<keyof T, string>]
        }
      } else if (sourceValue !== comparisonValue) {
        // Copy primitives only if source and comparison values differ
        result[key] = cloneDeep(sourceValue)
      }
    }
  }

  return result
}
